export type ModalidadeRecebimento = 'S' | 'P' | 'F';

export type RegimeIr = 'P' | 'R';

/**
 * R rascunho, A solicitado (em análise), P aguardando documentação, E documento reenviado, V documento validado,
 * L em processamento (integração), F erro ao processar, O concluído, C cancelado.
 */
export type StatusRequerimento = 'R' | 'A' | 'P' | 'E' | 'V' | 'L' | 'F' | 'O' | 'C';

/** Já enviado e ainda aberto (de A até F); o rascunho (R) não tem detalhes, e O e C já encerraram. */
export const STATUS_ENVIADA: readonly StatusRequerimento[] = ['A', 'P', 'E', 'V', 'L', 'F'];

/** O participante só cancela até o documento ser validado; de V em diante, não. */
export const STATUS_CANCELAVEL: readonly StatusRequerimento[] = ['R', 'A', 'P', 'E'];

/** Etapa do requerimento depois do aceite do termo (RequerimentoEtapas.EtapaTermo da API, PRE-204). */
export const ETAPA_TERMO = 7;

export interface ProblemaApi {
  title?: string;
  detail?: string;
  status?: number;
  /** Código estável do erro de negócio (ex.: BaseUnificadaDivergente no 409 do envio). */
  codigo?: string;
  errors?: Record<string, string[]>;
}

export interface RequerimentoApi {
  id: number;
  tp_status: StatusRequerimento;
  num_etapa_atual: number;
  num_protocolo: string | null;
  cadastro: CadastroApi | null;
  beneficio: BeneficioApi | null;
  tributacao: TributacaoApi | null;
}

/** E: elegível · R: elegível com restrições · N: não elegível. */
export type TipoElegibilidade = 'E' | 'R' | 'N';

/**
 * GET /v1/requerimentos/home (etapa 0): participante do token, elegibilidade, institutos que podem ser pedidos e o
 * requerimento aberto (R/A/P) para retomada, nulo quando não há.
 */
export interface HomeApi {
  participante: ParticipanteHomeApi;
  elegibilidade: ElegibilidadeApi;
  institutos_disponiveis: TipoInstitutoApi[];
  requerimento_em_andamento: RequerimentoApi | null;
}

export interface ParticipanteHomeApi {
  nome: string | null;
  cpf: string;
  cod_empresa: string;
  matricula: number;
  cod_plano: string | null;
  dt_nascimento: string | null;
}

export interface ElegibilidadeApi {
  tp_elegibilidade: TipoElegibilidade;
  /** Vazio quando elegível (E). */
  motivos: string[];
}

export interface TipoInstitutoApi {
  id: number;
  nome: string;
  nome_exibicao: string;
  fl_status: boolean;
}

/** Conta bancária já cadastrada do participante, oferecida para preencher os dados bancários da etapa 1. */
export interface ContaCadastrada {
  codBanco: number;
  nomeBanco: string;
  agencia: string;
  /** Número com o dígito separado por '-' (ex.: 8716287-6). */
  conta: string;
}

export interface CadastroApi {
  ds_email_princ: string;
  ds_email_sec: string | null;
  cod_ddd_celular: string | null;
  num_celular: string | null;
  cod_cep: string | null;
  cod_uf: string | null;
  nom_cidade: string | null;
  nom_bairro: string | null;
  ds_endereco: string | null;
  num_endereco: string | null;
  ds_complemento: string | null;
  fl_reside_exterior: boolean;
  nom_pais_exterior: string | null;
  cod_zip_exterior: string | null;
  num_nif_exterior: string | null;
  cod_banco: number | null;
  /** Nome do banco gravado pela API na jornada; só leitura (não vai no PATCH). */
  nom_banco?: string | null;
  /** Data da leitura da base unificada que deu a origem do envio; nula antes da primeira carga. Só leitura. */
  dt_origem_cadastro?: string | null;
  cod_agencia: string | null;
  /** Dígito da agência (1 caractere: número ou X), gravado pela carga da base unificada; vai de volta no PATCH. */
  cod_digito_agencia: string | null;
  num_conta: string | null;
  ds_digito_conta: string | null;
  /** Null = não respondida (a API não tem padrão). */
  fl_molestia_grave: boolean | null;
}

/** Conta da base unificada: a de maior id_dado_bancario é a mais recente. */
export interface ContaBaseApi {
  id_dado_bancario: number;
  codigo_banco: number | null;
  nome_banco: string | null;
  agencia: string | null;
  digito_agencia: string | null;
  conta: string | null;
  digito_conta: string | null;
}

/** Cadastro vigente da base unificada (CadastroBaseUnificada da API, em snake_case). */
export interface CadastroBaseApi {
  email_principal: string;
  email_secundario: string | null;
  ddd_celular: number | null;
  celular: number | null;
  cep: string | null;
  uf: string | null;
  cidade: string | null;
  bairro: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  residente_exterior: boolean;
  contas: ContaBaseApi[];
}

/** GET /v1/requerimentos/dados-cadastrais-base: cadastro vigente e as opções de banco e país, sem gravar nada. */
export interface DadosCadastraisBaseApi {
  cadastro: CadastroBaseApi;
  bancos: BancoApi[];
  paises: PaisApi[];
}

export interface BeneficioApi {
  pct_antecip_saldo: number | null;
  tp_modalidade_receb: ModalidadeRecebimento | null;
  pct_saldo_mensal: number | null;
  qtd_anos_prazo: number | null;
  vl_fixo_mensal: number | null;
  fl_adiant_benef: boolean;
}

export interface TributacaoApi {
  tp_regime_ir: RegimeIr | null;
  qtd_depend_ir: number;
}

/** GET /v1/requerimentos/{id}/termo e POST .../termo/aceite: sem aceite vigente, `aceite` vem nulo. */
export interface TermoApi {
  hash_termo: string;
  aceite: TermoAceiteApi | null;
}

export interface TermoAceiteApi {
  dt_assinatura: string;
  hash_resumo: string | null;
}

/** Lista do beneficiário na etapa 4: P (preferencial) ou D (designado). */
export type TipoListaBeneficiario = 'P' | 'D';

/** GET /v1/requerimentos/{id}/beneficiarios e respostas do POST e do PATCH. */
export interface BeneficiarioApi {
  id: number;
  id_reqr: number;
  tp_lista: TipoListaBeneficiario;
  nom_beneficiario: string;
  /** Somente os 11 dígitos. */
  num_cpf_benef: string;
  dt_nascimento: string;
  id_grau_parentesco: number | null;
  /** Rótulo do parentesco exibido ao participante. */
  ds_parentesco: string;
  pct_participacao: number;
  /** Removido da lista: a API não apaga, só inativa. */
  fl_ativo: boolean;
  dt_inclusao: string;
  dt_ult_atu: string;
}

/** GET /api-baseunificada-cadastro-parametro/graus-parentesco: opções do parentesco dos beneficiários. */
export interface GrauParentescoApi {
  id: number;
  descricao: string;
}

/** Opção do banco nos dados cadastrais (bancos do GET dados-cadastrais-base). */
export interface BancoApi {
  /** Código do banco (1 = Banco do Brasil). */
  codigo: number;
  nome: string;
}

/** Opção do país de residência no exterior (paises do GET dados-cadastrais-base). */
export interface PaisApi {
  id: number;
  nome: string;
  /** ISO de 2 letras (PT = Portugal). */
  sigla: string;
}

/** GET /v1/requerimentos/{id}/tributacao: IR simulado sobre o benefício configurado, por regime. */
export interface TributacaoSimuladaApi {
  qtd_depend_ir: number;
  valor_bruto: number;
  regimes: RegimeSimuladoApi[];
}

export interface RegimeSimuladoApi {
  tp_regime_ir: RegimeIr;
  /** Alíquota efetiva em pontos percentuais (15 = 15%). */
  aliquota_efetiva: number;
  valor_ir: number;
  valor_liquido: number;
  observacao: string | null;
}

/** GET /v1/requerimentos/{id}/simulacao: benefício recalculado com a modalidade da etapa 2, sem gravar. */
export interface SimulacaoBeneficioApi {
  valor_bruto: number;
  regimes: RegimeSimuladoApi[];
}

/** GET /v1/requerimentos/{id}/saldos: saldos do plano (Hub) e limites parametrizados da etapa 2. */
export interface SaldosRequerimentoApi {
  saldos: SaldosPlanoApi;
  limites: LimitesPlanoApi;
}

export interface SaldosPlanoApi {
  nome_plano: string;
  nome_patrocinadora: string;
  competencia: number;
  saldos_participante: SaldoParticipanteApi[];
  saldos_patrocinador: SaldoPatrocinadorApi[];
  total_participante: number;
  total_patrocinador: number;
  total: number;
  total_resgatavel: number;
}

export interface SaldoParticipanteApi {
  descricao_saldo: string;
  qtd_cotas: number;
  vlr_cotas: number;
  data_cotacao: string;
  valor: number;
  portabilidade: boolean;
}

export interface SaldoPatrocinadorApi {
  descricao_saldo: string;
  qtd_cotas: number;
  vlr_cotas: number;
  data_cotacao: string;
  valor: number;
  valor_resgatavel: number;
}

/** Sem parametrização gravada, a API devolve tudo desligado; os mínimos e máximos podem vir null. */
export interface LimitesPlanoApi {
  antecipacao: {
    fl_permite_antecip_sld: boolean;
    pc_min_antecip_sld: number | null;
    pc_max_antecip_sld: number | null;
    fl_elegivel_adiant: boolean;
    pc_adiant_benef: number | null;
  };
  opcoes_renda: {
    fl_vlr_fixo_mensal: boolean;
    fl_pct_saldo: boolean;
    pct_saldo_min: number | null;
    pct_saldo_max: number | null;
    fl_prazo_certo: boolean;
    qtd_prazo_min: number | null;
    qtd_prazo_max: number | null;
    tp_unid_prazo: string | null;
    fl_pagto_unico: boolean;
    fl_valida_vlr_min: boolean;
    tp_vlr_min: string | null;
    vlr_min_renda: number | null;
    pct_min_renda: number | null;
  };
}

/**
 * Valores do snapshot da etapa 6 que o POST e o PUT de /v1/requerimentos/{id}/resumo recebem (o PUT substitui
 * o resumo inteiro). Percentuais em pontos percentuais (15 = 15%); pct_aliquota_ir é a alíquota efetiva.
 */
export interface ResumoValoresApi {
  vl_cta_contrib_partic: number;
  vl_cta_contrib_patroc: number;
  pct_antecip_saldo: number;
  vl_antecip_saldo: number;
  vl_bruto_mensal: number;
  pct_aliquota_ir: number;
  vl_ir_mensal: number;
  vl_liquido_mensal: number;
  qtd_anos_estimado: number;
  qtd_parcelas: number | null;
  vl_prim_parcela: number | null;
  dt_prim_parcela: string | null;
  vl_bruto_resgate: number | null;
  vl_ir_resgate: number | null;
  vl_liquido_resgate: number | null;
  /** Response íntegro do serviço de cálculo (JSON em texto). */
  ds_retorno_calculo: string | null;
}

/** GET /v1/requerimentos/{id}/resumo: sem resumo gravado, a API responde 404. */
export interface ResumoApi extends ResumoValoresApi {
  id: number;
  id_reqr: number;
  dt_calculo: string;
  /** Preenchida na assinatura do termo: a partir dela o resumo não muda mais. */
  dt_congelamento: string | null;
  dt_inclusao: string;
  dt_ult_atu: string;
}

/** Body do POST /v1/requerimentos/{id}/beneficiarios. */
export type BeneficiarioPayload = Pick<
  BeneficiarioApi,
  | 'tp_lista'
  | 'nom_beneficiario'
  | 'num_cpf_benef'
  | 'dt_nascimento'
  | 'id_grau_parentesco'
  | 'ds_parentesco'
  | 'pct_participacao'
>;

/** Body do PATCH /v1/requerimentos/{id}/beneficiarios/{idBeneficiario}: campo ausente não muda. */
export type BeneficiarioPatchPayload = Partial<
  BeneficiarioPayload & Pick<BeneficiarioApi, 'fl_ativo'>
>;

export interface Requerimento {
  id: number;
  status: StatusRequerimento;
  etapaAtual: number;
  protocolo: string | null;
  cadastro: Cadastro | null;
  beneficio: Beneficio | null;
  tributacao: Tributacao | null;
}

export interface Home {
  participante: ParticipanteHome;
  elegibilidade: Elegibilidade;
  institutosDisponiveis: TipoInstituto[];
  requerimentoEmAndamento: Requerimento | null;
}

export interface ParticipanteHome {
  nome: string | null;
  cpf: string;
  codEmpresa: string;
  matricula: number;
  codPlano: string | null;
  dataNascimento: string | null;
}

export interface Elegibilidade {
  tipo: TipoElegibilidade;
  motivos: string[];
}

export interface TipoInstituto {
  id: number;
  nome: string;
  nomeExibicao: string;
  ativo: boolean;
}

/** Dados da base unificada para a etapa 1: o cadastro vigente, as contas do participante e as opções dos selects. */
export interface DadosCadastraisBase {
  cadastro: Cadastro;
  /** Da mais recente para a mais antiga. */
  contas: ContaCadastrada[];
  bancos: BancoApi[];
  paises: PaisApi[];
}

export interface Cadastro {
  emailPrincipal: string;
  emailSecundario: string | null;
  dddCelular: string | null;
  celular: string | null;
  cep: string | null;
  uf: string | null;
  cidade: string | null;
  bairro: string | null;
  endereco: string | null;
  numeroEndereco: string | null;
  complemento: string | null;
  resideExterior: boolean;
  paisExterior: string | null;
  zipExterior: string | null;
  nifExterior: string | null;
  codBanco: number | null;
  nomeBanco?: string | null;
  /** Agência com o dígito separado por '-' quando houver (ex.: 1234-X), como a conta. */
  agencia: string | null;
  conta: string | null;
  /** Origem do envio (só leitura); nula antes da primeira carga da base unificada. */
  dtOrigemCadastro?: string | null;
  /** Null = não respondida. */
  molestiaGrave: boolean | null;
}

export interface Beneficio {
  modalidade: ModalidadeRecebimento | null;
  percentualAntecipacaoSaldo: number | null;
  percentualSaldoMensal: number | null;
  qtdAnosPrazo: number | null;
  valorFixoMensal: number | null;
  adiantaBeneficio: boolean;
}

export interface Tributacao {
  regime: RegimeIr | null;
  qtdDependentes: number;
}

export interface Termo {
  /** SHA-256 do PDF exibido: volta no aceite para provar que o participante aceitou o que leu. */
  hashTermo: string;
  /** Já há aceite vigente: falta só o envio. */
  aceito: boolean;
  /** Data e hora do aceite vigente; null sem aceite. */
  dataAssinatura: string | null;
}

export interface TributacaoSimulada {
  valorBruto: number;
  regimes: RegimeSimulado[];
  /** Resposta íntegra da simulação (JSON em texto), guardada no resumo como ds_retorno_calculo. */
  retorno: string;
}

export interface SimulacaoBeneficio {
  valorBruto: number;
  regimes: RegimeSimulado[];
}

/** Saldos e limites que a etapa 2 usa. Percentuais em pontos percentuais (15 = 15%); prazos em anos. */
export interface SaldosRequerimento {
  saldoParticipante: number;
  saldoPatrocinadora: number;
  saldoTotal: number;
  limites: LimitesBeneficio;
}

export interface LimitesBeneficio {
  permiteAntecipacao: boolean;
  percentualMinimoAntecipacao: number | null;
  percentualMaximoAntecipacao: number | null;
  elegivelAdiantamento: boolean;
  permiteValorFixoMensal: boolean;
  permitePercentualSaldo: boolean;
  percentualMinimoMensal: number | null;
  percentualMaximoMensal: number | null;
  permitePrazoCerto: boolean;
  prazoMinimoAnos: number | null;
  prazoMaximoAnos: number | null;
}

export interface RegimeSimulado {
  regime: RegimeIr;
  /** Alíquota efetiva em pontos percentuais (15 = 15%). */
  aliquotaEfetiva: number;
  valorIr: number;
  valorLiquido: number;
  observacao: string | null;
}

/** Snapshot dos valores calculados da etapa 6. Percentuais em pontos percentuais (15 = 15%). */
export interface ResumoValores {
  saldoParticipante: number;
  saldoPatrocinadora: number;
  percentualAntecipacao: number;
  valorAntecipacao: number;
  valorBrutoMensal: number;
  /** Alíquota efetiva (IR / bruto × 100), não a de faixa. */
  percentualImpostoRenda: number;
  valorImpostoRenda: number;
  valorLiquidoMensal: number;
  tempoEstimadoAnos: number;
  qtdParcelas: number | null;
  valorPrimeiraParcela: number | null;
  dataPrimeiraParcela: string | null;
  valorBrutoResgate: number | null;
  valorImpostoRendaResgate: number | null;
  valorLiquidoResgate: number | null;
  retornoCalculo: string | null;
}

export interface Resumo extends ResumoValores {
  dataCalculo: string;
  /** Congelado na assinatura do termo: não aceita mais PUT. */
  congelado: boolean;
}

export interface BeneficioFormulario {
  modalidade: ModalidadeRecebimento;
  percentualAntecipacao: string;
  percentualMensal: string;
  prazoAnos: string;
  valorMensal: string;
  adiantaBeneficio: boolean;
}
